import { DataGrid, DataGridColumn, DataGridRef } from '@cimplico/components';
import { Ref, useState } from 'react';
import { ROW_SIZES } from '../../constants';
import { useEntities } from '../../queries/use-entities';
import { Entity } from '../../types/entity';
import EntityRow from './EntityRow';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import NoResults from '../../components/empty-state/NoResults';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import useResizeObserver from 'use-resize-observer';

const largeColumns: DataGridColumn[] = [
    {
        header: 'Entity',
        alignment: 'left',
        width: '25%',
    },
    {
        header: 'Type',
        alignment: 'left',
        width: '10%',
    },
    {
        header: 'Group',
        alignment: 'left',
        width: '15%',
    },
    {
        header: 'Last Updated',
        alignment: 'left',
        width: '12.5%',
    },
    {
        header: 'Open Workpapers',
        alignment: 'center',
        width: '15%',
    },
    {
        header: 'Integrations',
        alignment: 'center',
        width: '12.5%',
    },
];

const smallColumns: DataGridColumn[] = [
    {
        header: 'Entity',
        alignment: 'left',
        width: 'minmax(200px,2fr)',
    },
    {
        header: 'Updated',
        alignment: 'left',
        width: '1fr',
    },
];

type EntitiesTableProps = {
    search?: string;
    sort?: string;
    filter?: string;
    onSelect?: (entities: Entity[]) => void;
    tableRef?: Ref<DataGridRef> | null;
    emptyStateNavigateTo?: string;
};

function EntitiesTable({ emptyStateNavigateTo, search = '', sort = '', filter = '', onSelect, tableRef }: EntitiesTableProps): JSX.Element {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(ROW_SIZES[0]);
    const [showSmallColumns, setShowSmallColumns] = useState(false);

    const { isExternalUser } = useIsFirmRole();

    const entitiesQuery = useEntities({ page, limit, search, sort, filter });
    const TABLE_RESIZE_BREAKPOINT = 820;

    const { ref } = useResizeObserver<HTMLDivElement>({
        onResize: ({ width = 1 }) => {
            setTimeout(() => {
                if (width <= TABLE_RESIZE_BREAKPOINT) {
                    setShowSmallColumns(true);
                } else {
                    setShowSmallColumns(false);
                }
            }, 0);
        },
    });

    if (entitiesQuery.isLoading) {
        return <></>;
    }

    if (entitiesQuery.isError || !entitiesQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const { entities, totalItems } = entitiesQuery.data;

    function handleSelect(ids: string[]): void {
        const selectedEntities = entities.filter((e) => ids.includes(e.id)) ?? [];
        onSelect && onSelect(selectedEntities);
    }

    const showEmptyNew = !search;

    return (
        <div ref={ref}>
            <DataGrid
                ref={tableRef}
                columns={showSmallColumns ? smallColumns : largeColumns}
                rowSizes={ROW_SIZES}
                totalRows={totalItems}
                onPageChange={setPage}
                onRowSizeChange={setLimit}
                isLoading={entitiesQuery.isFetching}
                onSelect={handleSelect}
                emptyMessage={
                    showEmptyNew ? (
                        <EmptyStateNew
                            disableActionButton={isExternalUser}
                            icon={<BuildingOfficeIcon />}
                            navigateTo={emptyStateNavigateTo || '/entities/new'}
                            title="Add your first Entity"
                            actionText="New Entity"
                            helpLink="https://learn.cimplico.com/docs/creating-entities"
                            helpText="Entities in Cimplico Workpapers represent legal entities, such as companies, partnerships, trusts, and sole traders."
                        />
                    ) : (
                        <NoResults search={search} />
                    )
                }
            >
                {entities.map((entity) => {
                    return <EntityRow showSmallColumns={showSmallColumns} id={entity.id} key={entity.id} entity={entity} />;
                })}
            </DataGrid>
        </div>
    );
}

export default EntitiesTable;
